// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Box, Tabs, Tab, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  useGetDeviceTimeUsageDay,
  useGetDeviceTimeUsageWeek,
  useGetDeviceTimeUsageMonth,
  useGetDeviceTimeUsageYear,
} from '../../services/DeviceService'; 

import {
  useGetPersonTimeUsageDay,
  useGetPersonTimeUsageWeek,
  useGetPersonTimeUsageMonth,
  useGetPersonTimeUsageYear,
} from '../../services/PeopleService';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function formatMinutes(totalMinutes) {
  const safe = Number.isFinite(totalMinutes) ? totalMinutes : 0;
  const h = Math.floor(safe / 60);
  const m = Math.round(safe % 60);
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}min`;
}

const WEEKDAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const toMinutes = (v) => Number(v) || 0;
const asArray = (apiData) => (Array.isArray(apiData) ? apiData : apiData?.data ?? []);

function normalizeDay(apiData) {
  const items = asArray(apiData);
  const labels = items.map((it) => `${it.hour}h`);
  const values = items.map((it) => toMinutes(it.usageInMinutes));
  const total = values.reduce((acc, v) => acc + v, 0);
  return { labels, values, total };
}

function normalizeWeek(apiData) {
  const items = asArray(apiData);
  const labels = items.map((it) => WEEKDAY_NAMES[moment(it.day).day()]);
  const values = items.map((it) => toMinutes(it.usageInMinutes));
  const total = values.reduce((acc, v) => acc + v, 0);
  return { labels, values, total };
}

function normalizeMonth(apiData) {
  const items = asArray(apiData);
  const labels = items.map((it) => moment(it.day).format('DD/MM'));
  const values = items.map((it) => toMinutes(it.usageInMinutes));
  const total = values.reduce((acc, v) => acc + v, 0);
  return { labels, values, total };
}

function normalizeYear(apiData) {
  const items = asArray(apiData);
  const labels = items.map((it) => MONTH_NAMES[Number(it.month) - 1]);
  const values = items.map((it) => toMinutes(it.usageInMinutes));
  const total = values.reduce((acc, v) => acc + v, 0);
  return { labels, values, total };
}

const PERIOD_TABS = [
  { value: 'DAY', label: 'DIA' },
  { value: 'WEEK', label: 'SEMANA' },
  { value: 'MONTH', label: 'MÊS' },
  { value: 'YEAR', label: 'ANO' },
];

export default function UsageChart({ type = 'device', idUser, idRouter, macAddress = null, idPerson = null }) {
  const [period, setPeriod] = useState('DAY');
  const [refDate, setRefDate] = useState(moment());

  const dayParam = refDate.format('YYYY-MM-DD');
  const weekParam = refDate.isoWeek();
  const monthParam = refDate.month() + 1;
  const yearParam = refDate.year();

  const isPerson = type === 'person';

  const deviceDayApi = useGetDeviceTimeUsageDay(idUser, idRouter, macAddress, dayParam);
  const deviceWeekApi = useGetDeviceTimeUsageWeek(idUser, idRouter, macAddress, weekParam, yearParam);
  const deviceMonthApi = useGetDeviceTimeUsageMonth(idUser, idRouter, macAddress, monthParam, yearParam);
  const deviceYearApi = useGetDeviceTimeUsageYear(idUser, idRouter, macAddress, yearParam);

  const personDayApi = useGetPersonTimeUsageDay(idUser, idRouter, idPerson, dayParam);
  const personWeekApi = useGetPersonTimeUsageWeek(idUser, idRouter, idPerson, weekParam, yearParam);
  const personMonthApi = useGetPersonTimeUsageMonth(idUser, idRouter, idPerson, monthParam, yearParam);
  const personYearApi = useGetPersonTimeUsageYear(idUser, idRouter, idPerson, yearParam);

  const dayApi = isPerson ? personDayApi : deviceDayApi;
  const weekApi = isPerson ? personWeekApi : deviceWeekApi;
  const monthApi = isPerson ? personMonthApi : deviceMonthApi;
  const yearApi = isPerson ? personYearApi : deviceYearApi;

  const { data: dayData, loading: dayLoading, request: requestDay } = dayApi;
  const { data: weekData, loading: weekLoading, request: requestWeek } = weekApi;
  const { data: monthData, loading: monthLoading, request: requestMonth } = monthApi;
  const { data: yearData, loading: yearLoading, request: requestYear } = yearApi;

  useEffect(() => {
    const identifier = isPerson ? idPerson : macAddress;
    if (!idRouter || !identifier) return;

    if (period === 'DAY') requestDay();
    else if (period === 'WEEK') requestWeek();
    else if (period === 'MONTH') requestMonth();
    else if (period === 'YEAR') requestYear();
  }, [period, idRouter, macAddress, idPerson, dayParam, weekParam, monthParam, yearParam]);

  const { labels, values, total, loading } = useMemo(() => {
    switch (period) {
      case 'WEEK':
        return { ...normalizeWeek(weekData), loading: weekLoading };
      case 'MONTH':
        return { ...normalizeMonth(monthData), loading: monthLoading };
      case 'YEAR':
        return { ...normalizeYear(yearData), loading: yearLoading };
      case 'DAY':
      default:
        return { ...normalizeDay(dayData), loading: dayLoading };
    }
  }, [period, dayData, weekData, monthData, yearData, dayLoading, weekLoading, monthLoading, yearLoading]);

  const barThickness = period === 'DAY' ? 12 : period === 'WEEK' ? 28 : period === 'MONTH' ? 9 : 18;

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: '#3730a3',
        borderRadius: 2,
        barThickness: barThickness,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => formatMinutes(ctx.raw),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: period === 'DAY' || period === 'MONTH',
          maxTicksLimit: period === 'DAY' || period === 'MONTH' ? 8 : undefined,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        position: 'right',
        grid: { color: '#eee' },
        ticks: {
          stepSize: 60,
          callback: (v) => `${Math.round(v / 60)}h`,
        },
      },
    },
  };

  const shiftDate = (amount) => {
    setRefDate((prev) => {
      const clone = prev.clone();
      const unit = { DAY: 'day', WEEK: 'week', MONTH: 'month', YEAR: 'year' }[period];
      return clone.add(amount, unit);
    });
  };

  const dateLabel = () => {
    if (period === 'DAY') return refDate.format('DD/MM/YYYY');
    if (period === 'WEEK') {
      const start = refDate.clone().startOf('isoWeek');
      const end = refDate.clone().endOf('isoWeek');
      return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
    };
    if (period === 'MONTH') return refDate.format('MMMM/YYYY');
    return refDate.format('YYYY');
  };

  const periodLabel = { DAY: 'dia', WEEK: 'semana', MONTH: 'mês', YEAR: 'ano' }[period];

  return (
    <Box sx={{width: '380px', border: '2px solid #ebeaea', borderRadius: 4, p: 3, bgcolor: '#fff' }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#3730a3' }}>
        Horas de consumo
      </Typography>

      <Tabs
        value={period}
        onChange={(_, v) => setPeriod(v)}
        sx={{
          minHeight: 32,
          mb: 1,
          width: 'fit-content',
          '& .MuiTabs-flexContainer': {
            justifyContent: 'flex-start',
          },
        }}
      >
        {PERIOD_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} sx={{ minWidth: 'fit-content', minHeight: 32, fontWeight: 700, padding: 1,  }} />
        ))}
      </Tabs>

      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
        <IconButton size="small" onClick={() => shiftDate(-1)}>
          <ChevronLeftIcon fontSize="small" sx={{ fontSize: 14 }}/>
        </IconButton>
        <Typography variant="body1" sx={{fontWeight: 600}}>{dateLabel()}</Typography>
        <IconButton size="small" onClick={() => shiftDate(1)}>
          <ChevronRightIcon fontSize="small" sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      <Typography variant="h5" align="center" color='#564cd4' sx={{ mt: 1 }}>
        Duração do consumo por {periodLabel}
      </Typography>
      <Typography variant="h2" align="center" sx={{ fontWeight: 900, mb: 2, color: '#3730a3' }}>
        {formatMinutes(total)}
      </Typography>

      <Box sx={{ height: 220 }}>
        {loading ? (
          <Typography align="center">Carregando...</Typography>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </Box>
    </Box>
  );
}